import { apiClient } from '@/lib/api/client'
import { fetchBusinessUnitOptions, type SelectOption } from '@/lib/services/storeService'
import type { BusinessUnitFormData } from '@/lib/validations/businessUnit'

export interface BusinessUnitHierarchyNode {
  depth: number
  hasChildren: boolean
  childCount: number
}

function unwrapTreeList(payload: unknown): SelectOption[] {
  if (Array.isArray(payload)) return payload as SelectOption[]
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data: unknown }).data
    if (Array.isArray(data)) return data as SelectOption[]
  }
  return []
}

export interface BusinessUnitRecord {
  id: number
  parent_id?: number | null
  name: string
  business_code: string
  descriptions?: string | null
  active?: string | boolean
}

function normalizePayload(payload: BusinessUnitFormData) {
  return {
    parent_id: payload.parent_id ?? null,
    name: payload.name.trim(),
    business_code: payload.business_code.trim(),
    descriptions: payload.descriptions?.trim() || null,
    active: payload.active,
  }
}

export async function fetchBusinessUnit(id: number): Promise<BusinessUnitRecord> {
  const { data } = await apiClient.get(`/store-management/business-unit/${id}`)
  return (data?.data ?? data) as BusinessUnitRecord
}

export async function createBusinessUnit(payload: BusinessUnitFormData, clientCode?: string) {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.post('/store-management/business-unit', normalizePayload(payload), { headers })
  return data
}

export async function updateBusinessUnit(id: number, payload: BusinessUnitFormData, clientCode?: string) {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.put(`/store-management/business-unit/${id}`, normalizePayload(payload), { headers })
  return data
}

export async function fetchBusinessUnitTreeOptions(clientCode?: string) {
  return fetchBusinessUnitOptions(clientCode)
}

/** Flat index of hierarchy depth and child counts for list UI (from options tree). */
export async function fetchBusinessUnitHierarchyIndex(
  clientCode?: string
): Promise<Map<number, BusinessUnitHierarchyNode>> {
  const headers = clientCode ? { 'X-Client-Code': clientCode } : undefined
  const { data } = await apiClient.get('/options/business', { headers })
  const items = unwrapTreeList(data)
  const map = new Map<number, BusinessUnitHierarchyNode>()

  const walk = (nodes: SelectOption[], depth = 0) => {
    for (const node of nodes) {
      const children = node.children ?? []
      map.set(node.id, {
        depth,
        hasChildren: children.length > 0,
        childCount: children.length,
      })
      if (children.length) walk(children, depth + 1)
    }
  }

  walk(items)
  return map
}

